import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { MapPin, Eye, Plus, Lock, Rocket } from "lucide-react";
import CountryFlag from "../components/CountryFlag";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AnnonseListe() {
  const { t, i18n } = useTranslation();
  const { category, subcategory } = useParams();
  const [cats, setCats] = useState({ categories: [], can_post: false });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [fylke, setFylke] = useState("");
  const [text, setText] = useState("");

  // Oversettelsesoppslag (fallback til server-svar)
  const catLabel = (c) => c && (i18n.exists(`ads.category.${c.key}`) ? t(`ads.category.${c.key}`) : c.label);
  const subLabel = (s) => s && (i18n.exists(`ads.sub.${s.key}`) ? t(`ads.sub.${s.key}`) : s.label);

  useEffect(() => {
    api.get("/ads/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (subcategory) p.set("subcategory", subcategory);
    if (fylke) p.set("fylke", fylke);
    if (text.trim()) p.set("text", text.trim());
    if (sort) p.set("sort", sort);
    p.set("per_page", "24");
    api.get(`/ads?${p.toString()}`)
      .then((r) => { setResults(r.data.results); setTotal(r.data.total); })
      .catch(() => { setResults([]); setTotal(0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, subcategory, sort]);

  const cat = cats.categories.find((c) => c.key === category);
  const sub = cat?.subs.find((s) => s.key === subcategory);

  return (
    <Layout withSidebar={false}>
      <div className="font-meta mb-3">
        <Link to="/annonser" className="hover:text-[var(--cream)]">{t("ads.breadcrumb")}</Link>
        {cat && <> <span className="mx-2">/</span><Link to={`/annonser/${cat.key}`} className="hover:text-[var(--cream)]">{catLabel(cat)}</Link></>}
        {sub && <> <span className="mx-2">/</span><span className="text-[var(--cream)]">{subLabel(sub)}</span></>}
      </div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] leading-tight">
            {subLabel(sub) || catLabel(cat) || t("ads.title_default")}
          </h1>
          <div className="font-meta mt-2">
            {t(total === 1 ? "ads.ad_count_one" : "ads.ad_count_other", { count: total })}
          </div>
        </div>
        {cats.can_post && (
          <Link to="/annonser/ny" className="btn-magenta inline-flex items-center" data-testid="new-ad-btn">
            <Plus className="h-3.5 w-3.5 mr-2" /> {t("ads.publish_short")}
          </Link>
        )}
      </div>

      {/* Sub-pills hvis vi er på hoved-kategori-nivå */}
      {cat && !sub && (
        <div className="flex flex-wrap gap-1.5 mb-6">
          {cat.subs.map((s) => (
            <Link key={s.key} to={`/annonser/${cat.key}/${s.key}`}
                  className="font-meta text-[10px] px-3 py-1.5 border border-white/15 rounded-sm hover:border-[var(--magenta)]/60 hover:text-[var(--magenta)]">
              {subLabel(s)} ({s.count})
            </Link>
          ))}
        </div>
      )}

      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-3 mb-6 panel-galleri" style={{ padding: "12px 16px" }}>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
               placeholder={t("ads.search_text_placeholder")} className="input-editorial flex-1 min-w-[200px]" data-testid="ad-search" />
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="input-editorial w-44" data-testid="sort-select">
          <option value="newest">{t("ads.sort_newest")}</option>
          <option value="price_asc">{t("ads.sort_price_asc")}</option>
          <option value="price_desc">{t("ads.sort_price_desc")}</option>
        </select>
        <button onClick={load} className="btn-magenta" data-testid="filter-apply">{t("ads.search_btn")}</button>
      </div>

      {loading ? (
        <div className="font-fraunces italic text-[var(--cream-soft)]">{t("common.loading")}</div>
      ) : results.length === 0 ? (
        <div className="text-center py-16 font-fraunces italic text-[var(--cream-soft)]">{t("ads.no_ads_yet")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ads-grid">
          {results.map((ad) => {
            const firstImg = ad.image_paths?.[0];
            return (
              <Link key={ad.id} to={`/annonser/se/${ad.id}`} className={`relative border bg-[var(--noir-elev)]/30 transition-colors flex flex-col ${
                ad.is_boosted ? "border-[var(--magenta)]/60 bg-[var(--magenta)]/[0.04]" : "border-white/10 hover:border-[var(--magenta)]/60"
              }`} data-testid={`ad-${ad.id}`}>
                {ad.is_boosted && (
                  <span className="absolute top-2 left-2 z-10 font-meta text-[9px] inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--magenta)] text-black rounded-sm" data-testid={`boost-badge-${ad.id}`}>
                    <Rocket className="h-2.5 w-2.5" /> BOOST
                  </span>
                )}
                <div className="aspect-[4/3] bg-black flex items-center justify-center overflow-hidden">
                  {firstImg ? (
                    <img src={`${BACKEND}/api/ads/images/raw?path=${encodeURIComponent(firstImg)}`} alt={ad.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="font-fraunces italic text-[var(--cream-faint)]">{t("common.no_image")}</div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-fraunces italic text-xl text-[var(--cream)] leading-tight line-clamp-2">{ad.title}</h3>
                  <div className="font-meta mt-1 flex items-center gap-3 flex-wrap">
                    {ad.subcategory && <span>{i18n.exists(`ads.sub.${ad.subcategory}`) ? t(`ads.sub.${ad.subcategory}`) : ad.subcategory_label}</span>}
                    {(ad.fylke || ad.city || ad.country) && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-2.5 w-2.5" />
                        {ad.country && <CountryFlag iso={ad.country} size="xs" />}
                        {[ad.city, ad.fylke].filter(Boolean).join(", ")}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-end justify-between gap-2">
                    {ad.price != null ? (
                      <div className="font-fraunces italic text-2xl text-[var(--magenta)]">{ad.price} kr</div>
                    ) : <div />}
                    <div className="font-meta inline-flex items-center gap-2">
                      <Eye className="h-3 w-3" /> {ad.view_count || 0}
                      <span className="mx-1">·</span>
                      {formatRelative(ad.created_at)}
                    </div>
                  </div>
                  <div className="font-meta text-[10px] mt-1">
                    {t("common.by").toUpperCase()} <span className="text-[var(--cream)]">{ad.owner?.username || t("common.unknown")}</span>
                    {(ad.owner?.is_verified || ad.owner?.tier === "vip" || ad.owner?.tier === "creator") && (
                      <span className="ml-1.5 inline-flex items-center gap-1 text-[var(--cognac)]" title={t("common.verified")}>
                        <Lock className="h-2.5 w-2.5" /> {t("common.verified")}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
